import React, { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X, Plus, Pencil, Trash2, Loader2, Sparkles, Check, ArrowRight } from 'lucide-react';
import FormFieldError from '../Common/FormFieldError/FormFieldError';
import lookupService from '../../services/lookupService';
import IcdCodeSearchField from '../Common/IcdCodeSearchField/IcdCodeSearchField';
import {
  CHRONIC_CONDITION_PRESETS,
  DEFAULT_ICD_BY_CONDITION,
  EMPTY_PATIENT_CHRONIC_CONDITION,
  getDefaultIcdForCondition,
  getPatientChronicConditionKey,
  formatPatientChronicConditionSummary,
} from '../../utils/patientChronicConditionUtils';
import './PatientChronicConditionsSection.css';
import AppDateInput from '../Common/AppDateInput/AppDateInput';
import { matchesWordStart } from '../../utils/searchMatch';

const SEARCH_DEBOUNCE_MS = 250;

const getTodayDateString = () => {
  try {
    return new Date().toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const PatientChronicConditionsSection = ({
  conditions = [],
  onChange,
  minRequired = 2,
  maxAllowed = null,
  hasError = false,
  errorMessage = '',
  title = 'Chronic Disease Selection',
  description = 'Add qualifying chronic conditions. Click any disease preset, search live NIH ICD-10 database, or type a custom condition and press Enter.',
}) => {
  const [conditionSearch, setConditionSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_PATIENT_CHRONIC_CONDITION });
  const [formError, setFormError] = useState('');

  // Dropdown ref for robust outside-click detection
  const dropdownContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Dynamic NIH search state
  const [nihResults, setNihResults] = useState([]);
  const [isSearchingNih, setIsSearchingNih] = useState(false);
  const searchRequestIdRef = useRef(0);

  const safeConditions = useMemo(() => (Array.isArray(conditions) ? conditions : []), [conditions]);

  const hasMaxLimit = maxAllowed != null && Number.isFinite(Number(maxAllowed)) && Number(maxAllowed) > 0;
  const atCapacity = hasMaxLimit && safeConditions.length >= Number(maxAllowed);

  const existingKeys = useMemo(
    () =>
      new Set(
        safeConditions
          .map((c, idx) => (editingIndex === idx ? '' : getPatientChronicConditionKey(c)))
          .filter(Boolean),
      ),
    [safeConditions, editingIndex],
  );

  const existingIcds = useMemo(
    () =>
      new Set(
        safeConditions
          .map((c, idx) => (editingIndex === idx ? '' : String(c.icd_code || '').trim().toUpperCase()))
          .filter(Boolean),
      ),
    [safeConditions, editingIndex],
  );

  const filteredPresets = useMemo(() => {
    const q = conditionSearch.trim().toLowerCase();
    if (!q) return CHRONIC_CONDITION_PRESETS;
    return CHRONIC_CONDITION_PRESETS.filter((name) => {
      const defaultIcd = String(DEFAULT_ICD_BY_CONDITION[name] || '').toLowerCase();
      return matchesWordStart(name, conditionSearch) || name.toLowerCase().includes(q) || defaultIcd.includes(q);
    });
  }, [conditionSearch]);

  // Handle outside click to close dropdown cleanly
  useEffect(() => {
    if (!showDropdown) return undefined;

    const handleOutsideClick = (event) => {
      if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [showDropdown]);

  // Debounced NIH ICD-10 API lookup
  useEffect(() => {
    const q = conditionSearch.trim();
    if (!q || q.length < 2) {
      searchRequestIdRef.current += 1;
      setNihResults([]);
      setIsSearchingNih(false);
      return undefined;
    }

    const currentId = ++searchRequestIdRef.current;
    const timer = setTimeout(async () => {
      setIsSearchingNih(true);
      try {
        const res = await lookupService.searchIcdCodes(q);
        if (currentId !== searchRequestIdRef.current) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setNihResults(rows);
      } catch (err) {
        if (currentId !== searchRequestIdRef.current) return;
        setNihResults([]);
      } finally {
        if (currentId === searchRequestIdRef.current) {
          setIsSearchingNih(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [conditionSearch]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [showDropdown]);

  const determineDefaultBillingRole = (currentLength) => {
    if (currentLength === 0) return 'primary';
    if (currentLength === 1) return 'secondary';
    return 'additional';
  };

  /** Open modal to enter onset date and details before adding */
  const handleSelectCondition = (name, icd = '') => {
    const cleanName = String(name || '').trim();
    if (!cleanName) return;

    const key = cleanName.toLowerCase();
    if (existingKeys.has(key)) {
      return;
    }

    if (atCapacity) {
      return;
    }

    const resolvedIcd = String(icd || getDefaultIcdForCondition(cleanName) || '').trim();
    setEditingIndex(null);
    setFormData({
      ...EMPTY_PATIENT_CHRONIC_CONDITION,
      condition_name: cleanName,
      icd_code: resolvedIcd,
      severity: 'moderate',
      onset_date: getTodayDateString(),
      status: 'active',
      billing_role: determineDefaultBillingRole(safeConditions.length),
    });
    setFormError('');
    setShowDropdown(false);
    setShowFormModal(true);
    setConditionSearch('');
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const q = conditionSearch.trim();
      if (!q) return;

      if (filteredPresets.length > 0) {
        const topPreset = filteredPresets[0];
        handleSelectCondition(topPreset, DEFAULT_ICD_BY_CONDITION[topPreset] || '');
      } else if (nihResults.length > 0) {
        const topNih = nihResults[0];
        handleSelectCondition(topNih.name || topNih.code, topNih.code || '');
      } else {
        handleSelectCondition(q, getDefaultIcdForCondition(q) || '');
      }
    }
  };

  const openCustomDetailForm = () => {
    if (atCapacity) return;
    setEditingIndex(null);
    setFormData({
      ...EMPTY_PATIENT_CHRONIC_CONDITION,
      condition_name: conditionSearch.trim(),
      icd_code: getDefaultIcdForCondition(conditionSearch.trim()),
      onset_date: getTodayDateString(),
      billing_role: determineDefaultBillingRole(safeConditions.length),
    });
    setFormError('');
    setShowDropdown(false);
    setShowFormModal(true);
  };

  const openEditForm = (index) => {
    setEditingIndex(index);
    setFormData({ ...safeConditions[index] });
    setFormError('');
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingIndex(null);
    setFormData({ ...EMPTY_PATIENT_CHRONIC_CONDITION });
    setFormError('');
  };

  useEffect(() => {
    if (!showFormModal) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (document.querySelector('.react-datepicker')) return;
      event.stopPropagation();
      closeFormModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showFormModal]);

  const handleRemove = (index) => {
    if (typeof onChange === 'function') {
      onChange(safeConditions.filter((_, i) => i !== index));
    }
  };

  const handleSaveForm = () => {
    const condition_name = formData.condition_name?.trim();
    if (!condition_name) {
      setFormError('Condition name is required.');
      return;
    }

    const key = condition_name.toLowerCase();
    if (existingKeys.has(key)) {
      setFormError('This condition is already added.');
      return;
    }

    const nextEntry = {
      ...EMPTY_PATIENT_CHRONIC_CONDITION,
      ...formData,
      condition_name,
      icd_code: formData.icd_code?.trim() || '',
      onset_date: formData.onset_date || getTodayDateString(),
    };

    if (typeof onChange === 'function') {
      if (editingIndex != null) {
        const next = [...safeConditions];
        next[editingIndex] = nextEntry;
        onChange(next);
      } else {
        if (atCapacity) {
          setFormError(
            Number(maxAllowed) === 1
              ? 'PCM allows one principal chronic condition only.'
              : `You can add at most ${maxAllowed} conditions.`
          );
          return;
        }
        onChange([...safeConditions, nextEntry]);
      }
    }
    closeFormModal();
  };

  return (
    <div className="pcc-section">
      <h3 className="pcc-title">
        {title}
        {minRequired > 0 && <span className="pcc-required">*</span>}
      </h3>
      {description && <p className="pcc-description">{description}</p>}

      <div className={`pcc-search-dropdown-container ${hasError ? 'is-invalid' : ''}`} ref={dropdownContainerRef}>
        <button
          type="button"
          className="pcc-dropdown-trigger"
          onClick={() => setShowDropdown((prev) => !prev)}
        >
          <span className={safeConditions.length > 0 ? 'pcc-trigger-text--selected' : 'pcc-trigger-text--placeholder'}>
            {safeConditions.length > 0
              ? `${safeConditions.length} condition${safeConditions.length > 1 ? 's' : ''} added${atCapacity ? ' (maximum reached)' : ''}`
              : hasMaxLimit && Number(maxAllowed) === 1
                ? 'Select 1 principal chronic condition'
                : `Add qualifying chronic diseases (min. ${minRequired})`}
          </span>
          <ChevronDown size={18} className={`pcc-chevron-icon ${showDropdown ? 'rotate-180' : ''}`} />
        </button>

        {showDropdown && (
          <div className="pcc-dropdown-menu">
            <div className="pcc-dropdown-search-wrap">
              <Search size={16} className="pcc-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search disease name or ICD-10 code (e.g. diabetes, I10, asthma)..."
                value={conditionSearch}
                onChange={(e) => setConditionSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pcc-dropdown-search-input"
              />
              {isSearchingNih && (
                <Loader2 size={15} className="pcc-search-spinner" />
              )}
              {conditionSearch && !isSearchingNih && (
                <button
                  type="button"
                  onClick={() => setConditionSearch('')}
                  className="pcc-clear-search-btn"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {conditionSearch.trim() && (
              <div
                className="pcc-quick-add-banner"
                onClick={() => handleSelectCondition(conditionSearch.trim())}
                role="button"
                tabIndex={0}
              >
                <span>Press <kbd className="pcc-kbd">Enter</kbd> or click to add <strong>"{conditionSearch.trim()}"</strong></span>
                <ArrowRight size={14} />
              </div>
            )}

            <div className="pcc-dropdown-options-list">
              {/* 1. Common Preset Conditions */}
              {filteredPresets.length > 0 && (
                <div className="pcc-dropdown-group">
                  <div className="pcc-dropdown-group-header">
                    Common Presets ({filteredPresets.length})
                  </div>
                  {filteredPresets.map((presetName) => {
                    const isAdded = existingKeys.has(presetName.toLowerCase());
                    const defaultIcd = DEFAULT_ICD_BY_CONDITION[presetName];
                    const isDisabled = isAdded || atCapacity;

                    return (
                      <div
                        key={presetName}
                        role="button"
                        tabIndex={isDisabled ? -1 : 0}
                        className={`pcc-option-item ${isAdded ? 'is-selected' : ''} ${atCapacity && !isAdded ? 'is-disabled' : ''}`}
                        onClick={() => {
                          if (!isDisabled) {
                            handleSelectCondition(presetName, defaultIcd);
                          }
                        }}
                      >
                        <div className="pcc-option-info">
                          <span className="pcc-option-label">{presetName}</span>
                          {defaultIcd && <code className="pcc-option-icd-code">{defaultIcd}</code>}
                        </div>
                        {isAdded ? (
                          <span className="pcc-added-tag"><Check size={12} /> Added</span>
                        ) : (
                          <Plus size={14} className="pcc-add-icon" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 2. Dynamic NIH ICD-10 Search Results */}
              {nihResults.length > 0 && (
                <div className="pcc-dropdown-group">
                  <div className="pcc-dropdown-group-header pcc-nih-header">
                    <Sparkles size={12} className="pcc-nih-icon" />
                    Live NIH ICD-10 Results ({nihResults.length})
                  </div>
                  {nihResults.map((nihItem) => {
                    const codeUpper = String(nihItem.code || '').trim().toUpperCase();
                    const nameKey = String(nihItem.name || '').trim().toLowerCase();
                    const isAdded = (nameKey && existingKeys.has(nameKey)) || (codeUpper && existingIcds.has(codeUpper));
                    const isDisabled = isAdded || atCapacity;

                    return (
                      <div
                        key={nihItem.code || nihItem.name}
                        role="button"
                        tabIndex={isDisabled ? -1 : 0}
                        className={`pcc-option-item pcc-nih-option ${isAdded ? 'is-selected' : ''} ${atCapacity && !isAdded ? 'is-disabled' : ''}`}
                        onClick={() => {
                          if (!isDisabled) {
                            handleSelectCondition(nihItem.name || nihItem.code, nihItem.code || '');
                          }
                        }}
                      >
                        <div className="pcc-option-info">
                          <code className="pcc-nih-code-badge">{nihItem.code}</code>
                          <span className="pcc-nih-name">{nihItem.name}</span>
                        </div>
                        {isAdded ? (
                          <span className="pcc-added-tag"><Check size={12} /> Added</span>
                        ) : (
                          <Plus size={14} className="pcc-add-icon" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Loading state */}
              {isSearchingNih && (
                <div className="pcc-dropdown-loading">
                  <Loader2 size={16} className="pcc-search-spinner" />
                  <span>Searching NIH database…</span>
                </div>
              )}

              {/* No results */}
              {filteredPresets.length === 0 && nihResults.length === 0 && !isSearchingNih && (
                <div className="pcc-dropdown-no-results">
                  {conditionSearch.trim() ? (
                    <div>
                      <p>No exact preset found for "{conditionSearch}".</p>
                      <button
                        type="button"
                        className="pcc-add-custom-btn-inline"
                        onClick={() => handleSelectCondition(conditionSearch.trim())}
                      >
                        <Plus size={14} /> Add "{conditionSearch.trim()}" as custom disease
                      </button>
                    </div>
                  ) : (
                    'No diseases found'
                  )}
                </div>
              )}
            </div>

            <div className="pcc-dropdown-footer">
              <button
                type="button"
                className="pcc-add-custom-btn"
                onClick={openCustomDetailForm}
                disabled={atCapacity}
              >
                <Plus size={14} />
                Add custom condition with full details…
              </button>
            </div>
          </div>
        )}
      </div>

      <FormFieldError message={errorMessage} />

      {/* List of Added Conditions */}
      {safeConditions.length > 0 && (
        <div className="pcc-condition-list">
          {safeConditions.map((condition, index) => (
            <div key={`${getPatientChronicConditionKey(condition)}-${index}`} className="pcc-condition-card">
              <div className="pcc-condition-main">
                <div className="pcc-condition-title-row">
                  <span className="pcc-condition-name">{condition.condition_name}</span>
                  {condition.icd_code && (
                    <code className="pcc-nih-code-badge">{condition.icd_code}</code>
                  )}
                  {condition.billing_role && (
                    <span className={`pcc-role-badge pcc-role-${condition.billing_role}`}>
                      {condition.billing_role}
                    </span>
                  )}
                </div>
                <div className="pcc-condition-meta">{formatPatientChronicConditionSummary(condition)}</div>
              </div>
              <div className="pcc-condition-actions">
                <button
                  type="button"
                  className="pcc-icon-btn"
                  onClick={() => openEditForm(index)}
                  title="Edit condition details"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  className="pcc-icon-btn pcc-icon-btn--danger"
                  onClick={() => handleRemove(index)}
                  title="Remove condition"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit / Custom Detailed Modal (Overlay Portal) */}
      {showFormModal && typeof document !== 'undefined' && createPortal(
        <div className="pcc-form-overlay" onClick={closeFormModal}>
          <div
            className="pcc-form-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pcc-form-header">
              <h4>{editingIndex != null ? 'Edit Chronic Condition' : 'Add Chronic Condition'}</h4>
              <button type="button" className="pcc-form-close" onClick={closeFormModal}>
                <X size={18} />
              </button>
            </div>

            <div className="pcc-form-grid">
              <div className="pcc-form-group">
                <label className="pcc-label pcc-label--required">Condition Name</label>
                <input
                  className="pcc-input"
                  value={formData.condition_name}
                  onChange={(e) => setFormData((f) => ({ ...f, condition_name: e.target.value }))}
                  placeholder="e.g., Type 2 Diabetes"
                  autoFocus
                />
              </div>
              <div className="pcc-form-group">
                <label className="pcc-label">ICD-10 Code</label>
                <IcdCodeSearchField
                  value={formData.icd_code}
                  onChange={(code) => setFormData((f) => ({ ...f, icd_code: code }))}
                  placeholder="Search ICD-10 (e.g., E11.9, I10)..."
                  ariaLabel="ICD-10 code"
                />
              </div>
              <div className="pcc-form-group">
                <label className="pcc-label">Severity</label>
                <select
                  className="pcc-select"
                  value={formData.severity || 'moderate'}
                  onChange={(e) => setFormData((f) => ({ ...f, severity: e.target.value }))}
                >
                  <option value="mild">Mild</option>
                  <option value="moderate">Moderate</option>
                  <option value="severe">Severe</option>
                </select>
              </div>
              <div className="pcc-form-group">
                <label className="pcc-label">Date of diagnosis</label>
                <AppDateInput
                  className="pcc-input"
                  showCalendarIcon
                  value={formData.onset_date || getTodayDateString()}
                  onChange={(v) => setFormData((f) => ({ ...f, onset_date: v }))}
                />
              </div>
              <div className="pcc-form-group">
                <label className="pcc-label">Status</label>
                <select
                  className="pcc-select"
                  value={formData.status || 'active'}
                  onChange={(e) => setFormData((f) => ({ ...f, status: e.target.value }))}
                >
                  <option value="active">Active</option>
                  <option value="managed">Managed</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
              <div className="pcc-form-group">
                <label className="pcc-label">Billing Role</label>
                <select
                  className="pcc-select"
                  value={formData.billing_role || 'additional'}
                  onChange={(e) => setFormData((f) => ({ ...f, billing_role: e.target.value }))}
                >
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                  <option value="additional">Additional</option>
                </select>
              </div>
            </div>

            <FormFieldError message={formError} />

            <div className="pcc-form-footer">
              <button type="button" className="pcc-btn pcc-btn--ghost" onClick={closeFormModal}>
                Cancel
              </button>
              <button type="button" className="pcc-btn pcc-btn--primary" onClick={handleSaveForm}>
                {editingIndex != null ? 'Save Changes' : 'Add Condition'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PatientChronicConditionsSection;
